import { cn } from "../../lib/utils";
import { formatDate } from "../../helper/date-formatter.helper";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import LoadingBox from "../loading/LoadingBox";
import EmptyBox from "../emptystate/EmptyBox";

/**
 * @typedef {Object} AdditionalPrintSettingsProps
 *
 * @property {Object} deadlinesSettings
 * @property {boolean} deadlinesSettings.included
 * @property {string|null} deadlinesSettings.from
 * @property {string|null} deadlinesSettings.to
 *
 * @property {Object} approvalsSettings
 * @property {boolean} approvalsSettings.included
 * @property {boolean} approvalsSettings.isLoading
 * @property {Array<string|number>} approvalsSettings.list
 * @property {Array<string|number>} approvalsSettings.selected
 *
 * @property {(settings: {
 *   included?: boolean,
 *   from?: string | null,
 *   to?: string | null
 * }) => void} updateDeadlinesSettings
 *
 * @property {(settings: {
 *   included?: boolean,
 *   isLoading?: boolean,
 *   list?: Array<string|number>,
 *   selected?: Array<string|number>
 * }) => void} updateApprovalSettings
 *
 * @property {boolean} showApprovalsSelection
 *
 * @property {string} approvalDescription
 */
const AdditionalPrintSettings = ({
	deadlinesSettings,
	updateDeadlinesSettings,
	approvalsSettings,
	updateApprovalSettings,
	formClassName,
	approvalDescription,
	showApprovalsSelection,
	className,
	csvMode,
	readOnly,
}) => {
	const onApprovalChange = (id) => {
		let newArray = [...approvalsSettings.selected];
		if (approvalsSettings.selected.includes(id)) newArray = newArray.filter((item) => item !== id);
		else newArray.push(id);
		updateApprovalSettings({ selected: newArray });
	};

	return (
		<div className={cn("px-7 divide-y divide-dark-10", className)}>
			{/* Deadlines */}
			<div className={cn("py-6 global_form_landing", formClassName)}>
				<div className="font-semibold text-16 mb-5">Impostazioni scadenzario</div>

				{!csvMode && (
					<div className={cn("toggle_landing", readOnly && "opacity-70")}>
						<input
							type="checkbox"
							id="print-deadlines-toggle"
							checked={deadlinesSettings.included}
							className="landing"
							onChange={(e) => updateDeadlinesSettings({ included: e.target.checked })}
							disabled={readOnly}
						/>
						<label htmlFor="print-deadlines-toggle">Includi scadenzario</label>
					</div>
				)}

				{csvMode && (
					<div className="text-13 text-dark-70 -mt-3 fade_in">
						In fase di stampa in formato CSV è possibile filtrare le linee d’azione inserendo la
						data di inizio e la data di fine delle relative scadenze.
					</div>
				)}

				<div className="mt-4 flex gap-4 base">
					<div className="child">
						<label htmlFor="line_action_date_start" className="label_input">
							Data inizio
						</label>
						<div className="wrap_input_landing date w-full">
							<input
								max={deadlinesSettings.to && formatDate(deadlinesSettings.to, "YYYY-MM-DD")}
								value={deadlinesSettings.from || ""}
								onChange={(e) => updateDeadlinesSettings({ from: e.target.value })}
								disabled={(!deadlinesSettings.included && !csvMode) || readOnly}
								className={cn(
									"landing",
									((!deadlinesSettings.included && !csvMode) || readOnly) && "dis"
								)}
								id="line_action_date_start"
								type="date"
							/>
							<span className="icon_24 default calendar icon_mask !bg-interactive-100"></span>
						</div>
					</div>

					<div className="child">
						<label htmlFor="line_action_date_end" className="label_input">
							Data fine
						</label>
						<div className="wrap_input_landing date w-full">
							<input
								min={deadlinesSettings.from && formatDate(deadlinesSettings.from, "YYYY-MM-DD")}
								value={deadlinesSettings.to || ""}
								onChange={(e) => updateDeadlinesSettings({ to: e.target.value })}
								disabled={(!deadlinesSettings.included && !csvMode) || readOnly}
								className={cn(
									"landing",
									((!deadlinesSettings.included && !csvMode) || readOnly) && "dis"
								)}
								id="line_action_date_end"
								type="date"
							/>
							<span className="icon_24 default calendar icon_mask !bg-interactive-100"></span>
						</div>
					</div>
				</div>
			</div>

			{/* Approvals */}
			{!csvMode && (
				<div className="py-6">
					<div className="font-semibold text-16 mb-5">Impostazioni adozioni</div>
					<div className={cn("toggle_landing", readOnly && "opacity-70")}>
						<input
							type="checkbox"
							id="print-last-approval-toggle"
							className="landing"
							checked={approvalsSettings.included}
							onChange={(e) => updateApprovalSettings({ included: e.target.checked })}
							disabled={readOnly}
						/>
						<label htmlFor="print-last-approval-toggle">Includi adozioni</label>
					</div>

					{!!approvalsSettings.included && !!approvalDescription && (
						<div className="text-13 text-dark-70 mt-3 fade_in">{approvalDescription}</div>
					)}

					{!!approvalsSettings.included && showApprovalsSelection && (
						<div className="fade_in">
							{approvalsSettings.isLoading ? (
								<div className="border-y border-dark-10 mt-4">
									<LoadingBox blocked="true" classNames="!min-h-[100px]" />
								</div>
							) : (
								<div className="border-y border-dark-10 mt-4 fade_in">
									{approvalsSettings?.list?.length ? (
										<OverlayScrollbarsComponent
											defer
											options={{ scrollbars: { autoHide: "scroll" } }}
											className="max-h-[150px]"
										>
											<div className="grid gap-4 py-4">
												{approvalsSettings?.list?.map((item) => (
													<div className="flex" key={item.id}>
														<div className="check_landing small">
															<input
																checked={approvalsSettings?.selected?.includes(item.id)}
																type="checkbox"
																onChange={() => onApprovalChange(item.id)}
																id={item.id}
															/>
															<label htmlFor={item.id} className="icon">
																Adozione del {formatDate(item.date, "DD/MM/YYYY")} - {item.note}
															</label>
														</div>
													</div>
												))}
											</div>
										</OverlayScrollbarsComponent>
									) : (
										<EmptyBox
											small
											icon="icon_75 default approvals"
											title="Nessun adozione"
											description="Non ci sono adozioni per questo piano"
										/>
									)}
								</div>
							)}
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default AdditionalPrintSettings;
